import { addDays, differenceInDays, subDays } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as R from 'ramda';
import { memo, useRef } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { DateRangeFilterTestIds } from '../../../../../utils/testIds/DateRangeFilter.testIds';
import {
  calculateDate,
  getDateLabel,
} from '../../../../Crud/filters/hooks/dateRangeFilter/dateRangeFilter.utils';
import { useFilterV3Context } from '../../../contexts/FilterV3.context';
import { FilterOptions, FilterStep } from '../../../filter.types';
import FilterChip from '../../FilterChip/FilterChip';
import {
  DateRangeFilterValue,
  DateRangeValue,
  DateShortcut,
  DateShortcutRange,
  DateShortcutValue,
} from '../dateRangeFilter.types';

enum ArrowButton {
  PREVIOUS = -1,
  NEXT = 1,
}

interface MinimizedDateRangeFilterProps extends FilterOptions<DateRangeFilterValue> {
  value: DateRangeFilterValue;
  shortcuts: DateShortcutRange[];
  filterIndex?: number;
}

const MinimizedDateRangeFilter = ({
  onChange,
  value,
  shortcuts,
  filterIndex,
}: MinimizedDateRangeFilterProps) => {
  const { translate, locale } = useTranslation();
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const { finalDate, initialDate } = value as DateRangeValue;
  const { shortcut: selectedShortcut, shortcutStep } = value as DateShortcutValue;

  const currentShortcutStep = +shortcutStep || 0;

  const { onChangeAnchorEl, onChangeMenu } = useFilterV3Context();

  const selectedShortcutIndex = shortcuts.findIndex(({ name }) => name === selectedShortcut);
  const shortcut = shortcuts[selectedShortcutIndex];

  const getLabel = () => {
    const hasValue = initialDate || finalDate || selectedShortcut;
    if (!hasValue) {
      return translate(LangEnum.GLOBAL_DATE_ALL_PERIOD);
    }

    return getDateLabel(value, currentShortcutStep, locale, translate, shortcut?.name);
  };

  const handleOpenMenu = () => {
    if (anchorRef.current) {
      onChangeAnchorEl(anchorRef.current);
      onChangeMenu({
        step: FilterStep.SECOND_STEP,
        filterIndex,
      });
    }
  };

  const handleClick = (step: number) => {
    const newStep = currentShortcutStep + step;
    const isStepZero = step === 0;
    const isIncreasing = newStep > currentShortcutStep;

    if (!shortcut && finalDate && initialDate) {
      const delta = differenceInDays(finalDate, initialDate);
      const newFinalDate = isStepZero
        ? finalDate
        : isIncreasing
          ? addDays(finalDate, delta)
          : subDays(finalDate, delta);

      const newInitialDate = isStepZero
        ? initialDate
        : isIncreasing
          ? addDays(initialDate, delta)
          : subDays(initialDate, delta);

      onChange({
        finalDate: newFinalDate,
        initialDate: newInitialDate,
        shortcut: undefined,
        shortcutStep: newStep,
      });

      return;
    }

    const calculated = calculateDate(value, newStep, shortcut?.name);
    onChange({
      finalDate: calculated.finalDate,
      initialDate: calculated.initialDate,
      shortcut: shortcut?.name,
      shortcutStep: newStep,
    });
  };

  const label = getLabel();

  const isArrowButtonDisabled =
    shortcut?.name === DateShortcut.ALL_PERIOD ||
    (!finalDate && !initialDate && !shortcut) ||
    R.isEmpty(label);
  return (
    <FilterChip
      ref={anchorRef}
      primaryLabel={R.isEmpty(label) ? translate(LangEnum.GLOBAL_INVALID_DATES) : label}
      testId={DateRangeFilterTestIds.MINIMIZED_BUTTON}
      onLabelClick={handleOpenMenu}
      firstAction={{
        icon: ChevronLeft,
        onClick: () => handleClick(ArrowButton.PREVIOUS),
        disabled: isArrowButtonDisabled,
      }}
      secondAction={{
        icon: ChevronRight,
        onClick: () => handleClick(ArrowButton.NEXT),
        disabled: isArrowButtonDisabled,
      }}
    />
  );
};

export default memo(MinimizedDateRangeFilter);
